import React, { useState, useEffect } from 'react';
import { database, ref, get } from '../../firebase';
import { 
  TrendingUp, 
  RefreshCw,
  Search,
  Users,
  DollarSign,
  Shield,
  Calendar,
  Clock,
  ArrowUpRight
} from 'lucide-react';

const ROIReport = () => {
  const [roiData, setRoiData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [stats, setStats] = useState({
    totalROI: 0,
    totalUsers: 0,
    avgROI: 0,
    highestROI: 0,
    totalPackages: 0
  });

  useEffect(() => {
    fetchROIData();
  }, []);

  const fetchROIData = async () => {
    try {
      setLoading(true);
      
      const usersList = [];
      let totalROI = 0;
      let highestROI = 0;
      let usersWithROI = 0;
      let totalPackages = 0;

      // Fetch all users
      const usersRef = ref(database, 'users');
      const usersSnapshot = await get(usersRef);
      
      if (usersSnapshot.exists()) {
        const usersData = usersSnapshot.val();
        
        // Iterate through all registration dates
        Object.entries(usersData).forEach(([regDate, dateUsers]) => {
          if (!/^\d{4}-\d{2}-\d{2}$/.test(regDate) || !dateUsers || typeof dateUsers !== 'object') return;
          
          // Iterate through all users in that date
          Object.entries(dateUsers).forEach(([userKey, userData]) => {
            if (!userData || typeof userData !== 'object') return;
            
            const userId = userData.userId || userData.sponsorId || userKey;
            const userName = userData.name || userData.userName || 'Unknown';
            const roiBalance =
              parseFloat(userData.wallets?.roi) ||
              parseFloat(userData.wallets?.roiWallet) ||
              parseFloat(userData.roiWallet) ||
              parseFloat(userData.roiIncome) ||
              parseFloat(userData.wallets?.roiIncome) ||
              0;
            const packages = userData.packages || [];
            const activePackages = packages.filter(p => p.status === 'active').length;
            
            if (roiBalance > 0) {
              usersWithROI++;
              totalROI += roiBalance;
              if (roiBalance > highestROI) {
                highestROI = roiBalance;
              }
            }
            
            if (activePackages > 0) {
              totalPackages += activePackages;
            }
            
            usersList.push({
              id: userKey,
              userId: userId,
              userName: userName,
              roiBalance: roiBalance,
              activePackages: activePackages,
              totalPackages: packages.length,
              registrationDate: regDate,
              lastROI: userData.lastROICredit || null
            });
          });
        });
      }

      // Sort by ROI balance (highest first)
      usersList.sort((a, b) => b.roiBalance - a.roiBalance);

      setRoiData(usersList);
      setStats({
        totalROI,
        totalUsers: usersWithROI,
        avgROI: usersWithROI > 0 ? totalROI / usersWithROI : 0,
        highestROI,
        totalPackages
      });
    } catch (error) {
      console.error('Error fetching ROI data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  const filterData = () => {
    if (!searchTerm) return roiData;
    
    const term = searchTerm.toLowerCase();
    return roiData.filter(item =>
      item.userName?.toLowerCase().includes(term) ||
      item.userId?.toLowerCase().includes(term)
    );
  };

  const filteredData = filterData();

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 pb-8">
      <h1 className="text-2xl font-bold text-white mb-2">ROI Wallet Report</h1>
      <p className="text-gray-400 mb-6">All users ROI wallet balances</p>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <div className="bg-green-500/10 rounded-xl p-4 border border-green-500/30">
          <div className="text-green-400 text-xs mb-1">Total ROI</div>
          <div className="text-2xl font-bold text-white">{formatCurrency(stats.totalROI)}</div>
        </div>
        <div className="bg-blue-500/10 rounded-xl p-4 border border-blue-500/30">
          <div className="text-blue-400 text-xs mb-1">Users with ROI</div>
          <div className="text-2xl font-bold text-white">{stats.totalUsers}</div>
        </div>
        <div className="bg-yellow-500/10 rounded-xl p-4 border border-yellow-500/30">
          <div className="text-yellow-400 text-xs mb-1">Active Packages</div>
          <div className="text-2xl font-bold text-white">{stats.totalPackages}</div>
        </div>
        <div className="bg-purple-500/10 rounded-xl p-4 border border-purple-500/30">
          <div className="text-purple-400 text-xs mb-1">Highest Balance</div>
          <div className="text-2xl font-bold text-white">{formatCurrency(stats.highestROI)}</div>
        </div>
      </div>

      {/* Search */}
      <div className="bg-gray-800/30 rounded-xl p-4 mb-6 border border-gray-700">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by user name or ID..."
                className="w-full pl-10 pr-4 py-3 bg-gray-900 border border-gray-700 rounded-lg text-white placeholder-gray-500"
              />
            </div>
          </div>
          
          <button
            onClick={fetchROIData}
            className="px-4 py-3 bg-primary hover:bg-primary/80 rounded-lg text-white flex items-center gap-2"
          >
            <RefreshCw className="h-4 w-4" />
            Refresh
          </button>
        </div>
      </div>

      {/* Users Table */}
      <div className="bg-gray-800/30 rounded-xl border border-gray-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-900/50">
              <tr className="text-sm">
                <th className="py-4 px-4 text-left text-gray-400 font-medium">User</th>
                <th className="py-4 px-4 text-left text-gray-400 font-medium">User ID</th>
                <th className="py-4 px-4 text-left text-gray-400 font-medium">ROI Balance</th>
                <th className="py-4 px-4 text-left text-gray-400 font-medium">Active Packages</th>
                <th className="py-4 px-4 text-left text-gray-400 font-medium">Status</th>
                <th className="py-4 px-4 text-left text-gray-400 font-medium">Registered</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700/30">
              {filteredData.map((user) => (
                <tr key={user.id} className="hover:bg-gray-700/20 transition-colors">
                  <td className="py-4 px-4">
                    <div className="text-white font-medium">{user.userName}</div>
                  </td>
                  <td className="py-4 px-4">
                    <div className="text-gray-300 font-mono text-sm">{user.userId}</div>
                  </td>
                  <td className="py-4 px-4">
                    <span className={`font-bold ${user.roiBalance > 0 ? 'text-green-400' : 'text-gray-500'}`}>
                      {formatCurrency(user.roiBalance)}
                    </span>
                  </td>
                  <td className="py-4 px-4">
                    <span className="text-white">
                      {user.activePackages}
                    </span>
                  </td>
                  <td className="py-4 px-4">
                    {user.roiBalance > 0 ? (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-green-500/20 text-green-400 border border-green-500/30">
                        <ArrowUpRight className="h-3 w-3 mr-1" />
                        Active
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-gray-500/20 text-gray-400 border border-gray-500/30">
                        Zero Balance
                      </span>
                    )}
                  </td>
                  <td className="py-4 px-4">
                    <div className="text-gray-400 text-sm">
                      {new Date(user.registrationDate).toLocaleDateString()}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Summary */}
      {filteredData.length > 0 && (
        <div className="mt-4 text-sm text-gray-400">
          Showing {filteredData.length} users • {filteredData.filter(u => u.roiBalance > 0).length} with balance • Avg: {formatCurrency(stats.avgROI)}
        </div>
      )}
    </div>
  );
};

export default ROIReport;
